import { useState, useRef, useCallback, useEffect } from 'react';

const useAudioPlayer = (url: string) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState<number>(0);

  useEffect(() => {
    const audioElement = audioRef.current;

    const handleLoadedMetadata = () => {
      if (audioElement) {
        const seconds = audioElement.duration;
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        setDuration(`${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`);
      }
    };

    const handleTimeUpdate = () => {
      if (audioElement) {
        setCurrentTime(audioElement.currentTime);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    if (audioElement) {
      audioElement.addEventListener('loadedmetadata', handleLoadedMetadata);
      audioElement.addEventListener('timeupdate', handleTimeUpdate);
      audioElement.addEventListener('ended', handleEnded);
    }

    return () => {
      if (audioElement) {
        audioElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audioElement.removeEventListener('timeupdate', handleTimeUpdate);
        audioElement.removeEventListener('ended', handleEnded);
      }
    };
  }, []);

  const handleToggleAudio = useCallback(() => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  }, [isPlaying]);

  const handleSeek = useCallback((event: any, newValue: number | number[]) => {
    event.stopPropagation();
    if (!audioRef.current) return;
    const newTime = Array.isArray(newValue) ? newValue[0] : newValue;
    audioRef.current.currentTime = newTime;
  }, []);

  return {
    audioRef,
    isPlaying,
    duration,
    currentTime,
    handleToggleAudio,
    handleSeek,
  };
};

export default useAudioPlayer;
